import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { Button } from '../../components/ui/Button';
import { TextInput } from '../../components/ui/form/TextInput';
import { api } from '../../lib/api';
import { useSession } from '../../stores/session';

/**
 * Sending a host money.
 *
 * ## It only appears when the money can actually arrive
 *
 * `can_be_tipped` on the profile is three questions collapsed into one - the
 * feature is on, they are a host, and Stripe says their account can receive
 * payouts. A button that appears otherwise is a button that fails when pressed,
 * which is worse than no button at all: the person who pressed it wanted to
 * give somebody money and now thinks the site is broken.
 *
 * ## The amounts are presets, and the smallest is the floor
 *
 * Typing a number is possible and deliberately not the default. Most people
 * tipping want to pick one and move on, and a bare field makes them decide what
 * is normal here - which is a question a first-time tipper answers by closing
 * the dialog.
 *
 * The floor is real rather than decorative: below roughly $1.75 the platform's
 * share does not cover Stripe's fee on the payment, so a small tip costs money
 * to accept. It comes from the server, which owns the number.
 */
export function TipButton({
  username,
  displayName,
  minimumCents = 500,
}: {
  username: string;
  displayName: string;
  /** The floor, from platform settings. */
  minimumCents?: number;
}) {
  const me = useSession((state) => state.user);
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState('');
  const root = useRef<HTMLDivElement | null>(null);

  const presets = [minimumCents, minimumCents * 2, minimumCents * 4];

  const send = useMutation({
    mutationFn: (amountCents: number) =>
      api.post<{ checkout_url: string }>(`/users/${username}/tip`, { amount_cents: amountCents }),
    onSuccess: (result) => {
      /*
       * A full navigation, not a new tab.
       *
       * Stripe's page returns the customer to us afterwards, and a popup that
       * gets blocked - or closed - loses the payment with no way back to it.
       */
      window.location.href = result.checkout_url;
    },
  });

  /* Escape, and a click anywhere else. Same idiom as the other popovers. */
  useEffect(() => {
    if (!open) return;

    const away = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };

    const key = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('pointerdown', away);
    document.addEventListener('keydown', key);

    return () => {
      document.removeEventListener('pointerdown', away);
      document.removeEventListener('keydown', key);
    };
  }, [open]);

  // Nobody tips themselves, and a signed-out visitor has nothing to pay with.
  if (!me || me.username === username) {
    return null;
  }

  const typed = Math.round(Number(custom) * 100);
  const customValid = Number.isFinite(typed) && typed >= minimumCents;

  return (
    <span className="relative inline-flex" ref={root}>
      <Button
        variant="secondary"
        onClick={() => setOpen((was) => !was)}
        aria-expanded={open}
        data-tip-button
      >
        Tip
      </Button>

      {open && (
        <div
          data-tip-menu
          /*
            Downward, and anchored right.

            It opened upward when it lived in the listener action row near the
            bottom of the screen. In the status row it sits a hundred pixels
            from the top of the page, so opening upward put the whole panel
            off-screen - visible as a sliver above the viewport and otherwise
            unreachable.
          */
          className="absolute top-[calc(100%+8px)] right-0 z-20 w-[260px] rounded-[var(--radius-panel)] border border-line-lit bg-raised p-3 shadow-2xl"
        >
          <p className="mb-2 text-[13px] text-soft">
            Send {displayName} a tip. They keep most of it — the rest covers
            processing and the platform.
          </p>

          <div className="mb-2 flex gap-2">
            {presets.map((cents) => (
              <Button
                key={cents}
                variant="secondary"
                className="flex-1"
                disabled={send.isPending}
                onClick={() => send.mutate(cents)}
                data-tip-amount={cents}
              >
                ${(cents / 100).toFixed(0)}
              </Button>
            ))}
          </div>

          <form
            className="flex items-center gap-2"
            onSubmit={(event) => {
              event.preventDefault();

              if (customValid && !send.isPending) send.mutate(typed);
            }}
          >
            <span className="text-sm text-faint">$</span>
            <TextInput
              value={custom}
              onChange={(event) => setCustom(event.target.value)}
              inputMode="decimal"
              placeholder="Other"
              aria-label={`Tip ${displayName} another amount`}
              invalid={custom !== '' && !customValid}
              data-tip-custom
              className="min-w-0 flex-1"
            />
            <Button type="submit" disabled={!customValid || send.isPending} data-tip-send>
              Send
            </Button>
          </form>

          {send.isError && (
            <p className="mt-2 text-[11px] text-danger">
              That did not go through. They may not be set up to receive tips
              right now.
            </p>
          )}

          <p className="mt-2 text-[10px] text-ghost">
            Minimum ${(minimumCents / 100).toFixed(2)}. Payment is handled by
            Stripe; we never see your card.
          </p>
        </div>
      )}
    </span>
  );
}
